import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import BottomDrawer from '../../../../../../common/_custom/BottomDrawer/BottomDrawer';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import SortIcon from '../../../../../Investor/Deals/components/DealsList/DealsTable/components/SortIcon/SortIcon';
import TimePeriodFilter from '../../../BankDataTable/components/TimePeriodFilter/TimePeriodFilter';
import TypeSelector from '../../../../components/TypeSelector/TypeSelector';
import { SortConfig } from '../../../../../../utils/constants/commonConstants';
import { nextSortConfig } from '../../../../../../utils/sortHeaderUtils';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { AICA_CASHFLOW_BANKING_TRANSACTIONS_SORTED } from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { formatDayShortMonthYear, getLastCompletedMonthKey } from '../../../../../../utils/dateUtils';
import { OrgEntity } from '../../../../../../store/dashboard/financials/entityRationaleServices';
import { BankTransaction } from './types';
import { buildDrillDownView, groupTransactionsByMonth, sumTransactionTotals } from './entityDrillDownGrouping';
import { EN_IN_INT } from './cashflowLayout';
import { DRILL_DOWN_SORT_ANALYTICS_COLUMNS, DRILL_DOWN_SORT_CLEARED, DRILL_DOWN_SORT_FIELDS } from './constants';
import { useDrillDownDateRange } from './useDrillDownDateRange';
import { CFB_FILTER_LOCATIONS, CFB_QUICK_RANGES, QUICK_RANGE_KEYS } from '../../../BankDataTable/constants';
import { createCfbFilterTracking } from '../../../BankDataTable/cfbUsageTracking';
import DrawerBulkEditFooter, { DrawerBulkEdit } from './DrawerBulkEditFooter';
import SearchIcon from '../../../../../../assets/Svg/SearchIcon';
import styles from './EntityDrillDownDrawer.module.scss';

/** Maximum transactions that may be submitted in a single bulk edit. */
const MAX_BULK_SELECTION = 2000;

/** Type guard: narrows a drill-down row to the txn variant. */
function isTxnRow<R extends { kind: string }>(r: R): r is Extract<R, { kind: 'txn' }> {
    return r.kind === 'txn';
}

interface EntityDrillDownDrawerProps {
    entityName: string;
    entityId?: number;
    transactions: BankTransaction[];
    loading: boolean;
    onClose: () => void;
    /** When provided, the drawer renders edit affordances (checkboxes + footer). */
    canEdit?: boolean;
    orgEntities?: OrgEntity[];
    rationaleOptions?: string[];
    onSave?: (entityId: number | null, selectedIds: number[], edit: DrawerBulkEdit) => Promise<void>;
}

/** Plain en-IN formatted absolute value, rounded to whole rupees; '-' when not a finite number. No ₹ prefix. */
function formatTxnAmount(value: number | string): string {
    const n = typeof value === 'string' ? parseFloat(value) : value;
    if (!Number.isFinite(n)) return '-';
    return EN_IN_INT.format(Math.abs(n));
}

/** ₹-prefixed en-IN formatted absolute value, rounded to whole rupees, for subtotal/grand-total cells. */
function formatAmount(value: number): string {
    return `₹${EN_IN_INT.format(Math.abs(value))}`;
}

/** ₹-prefixed en-IN net amount, rounded to whole rupees, that preserves sign (negative nets show a leading '-'). */
function formatNetAmount(value: number): string {
    const amount = EN_IN_INT.format(Math.abs(value));
    return value < 0 ? `-₹${amount}` : `₹${amount}`;
}

function EntityDrillDownDrawer({
    entityName,
    entityId,
    transactions,
    loading,
    onClose,
    canEdit = false,
    orgEntities = [],
    rationaleOptions = [],
    onSave,
}: EntityDrillDownDrawerProps) {
    const grouped = useMemo(() => groupTransactionsByMonth(transactions), [transactions]);
    const { groups } = grouped;
    const datedMonthKeys = useMemo(() => groups.map((g) => g.monthKey).filter(Boolean), [groups]);
    const dateRangeBounds = {
        floor: datedMonthKeys[0] || getLastCompletedMonthKey(),
        ceiling: datedMonthKeys[datedMonthKeys.length - 1] || getLastCompletedMonthKey(),
    };
    const dateRange = useDrillDownDateRange(entityName, dateRangeBounds);
    const [sortConfig, setSortConfig] = useState<SortConfig | undefined>(undefined);
    const [searchText, setSearchText] = useState('');
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [bulkEdit, setBulkEdit] = useState<DrawerBulkEdit>({});
    const [saving, setSaving] = useState(false);

    // True only when a real save target exists — gates checkboxes and footer.
    const canEditFull = canEdit && !!onSave;

    // Reset selection and edit state when the entity changes (prevents stale ids reaching onSave).
    useEffect(() => {
        setSelectedIds(new Set());
        setBulkEdit({});
    }, [entityId]);

    const onSort = (field: string) => {
        const next = nextSortConfig(sortConfig, field);
        Mixpanel.track(AICA_CASHFLOW_BANKING_TRANSACTIONS_SORTED, {
            column: DRILL_DOWN_SORT_ANALYTICS_COLUMNS[field as keyof typeof DRILL_DOWN_SORT_ANALYTICS_COLUMNS],
            direction: (next?.direction ?? DRILL_DOWN_SORT_CLEARED).toLowerCase(),
        });
        setSortConfig(next);
    };

    const filterTracking = createCfbFilterTracking(CFB_FILTER_LOCATIONS.DRAWER, {
        quickRangeHandlers: {
            [QUICK_RANGE_KEYS.LAST_FY]: dateRange.selectLastFY,
            [QUICK_RANGE_KEYS.LAST_12M]: dateRange.selectLast12M,
            [QUICK_RANGE_KEYS.CURRENT_FY]: dateRange.selectCurrentFY,
        },
        activeQuickRange: dateRange.activeQuickRange,
        startMonth: dateRange.startMonth,
        endMonth: dateRange.endMonth,
        selectStartMonth: dateRange.selectStartMonth,
        selectEndMonth: dateRange.selectEndMonth,
    });

    const view = useMemo(
        () => buildDrillDownView(grouped, { startMonth: dateRange.startMonth, endMonth: dateRange.endMonth }, sortConfig),
        [grouped, dateRange.startMonth, dateRange.endMonth, sortConfig],
    );

    const filteredRows = useMemo(() => {
        if (!searchText) return view.rows;
        const lower = searchText.toLowerCase();
        return view.rows.filter((row) => row.kind !== 'txn' || row.txn.transactionNote.toLowerCase().includes(lower));
    }, [view.rows, searchText]);

    const visibleTxns = useMemo(() => filteredRows.filter(isTxnRow), [filteredRows]);
    const visibleTxnIds = useMemo(() => visibleTxns.map((r) => r.txn.id), [visibleTxns]);

    // Prune selectedIds to only the currently-visible filtered txn ids.
    useEffect(() => {
        const visibleIdSet = new Set(visibleTxnIds);
        setSelectedIds((prev) => {
            const pruned = new Set([...prev].filter((id) => visibleIdSet.has(id)));
            return pruned.size === prev.size ? prev : pruned;
        });
    }, [visibleTxnIds]);

    const filteredTotals = useMemo(() => sumTransactionTotals(visibleTxns.map((r) => r.txn)), [visibleTxns]);

    const showSubtotals = !searchText && !sortConfig?.direction;

    const handleClose = () => {
        setSelectedIds(new Set());
        setBulkEdit({});
        onClose();
    };

    /** Add or remove a set of ids from the selection (toggles: removes all if all present, else adds all). */
    const toggleIdGroup = (ids: number[]) => {
        const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allSelected) {
                ids.forEach((id) => next.delete(id));
            } else {
                ids.forEach((id) => next.add(id));
            }
            return next;
        });
    };

    const toggleMonthGroup = (monthKey: string) => {
        // Derive selectable ids from the filtered/visible rows only, so a search or
        // type filter doesn't cause hidden transactions to be included in the selection.
        const monthTxnIdSet = new Set(view.visibleGroups.find((g) => g.monthKey === monthKey)?.transactions.map((t) => t.id) ?? []);
        const monthTxnIds = visibleTxns.filter((r) => monthTxnIdSet.has(r.txn.id)).map((r) => r.txn.id);
        toggleIdGroup(monthTxnIds);
    };

    const toggleTxn = (id: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleCancel = () => {
        setSelectedIds(new Set());
        setBulkEdit({});
    };

    const handleSave = async () => {
        if (!onSave || selectedIds.size === 0) return;
        setSaving(true);
        try {
            await onSave(entityId ?? null, Array.from(selectedIds), bulkEdit);
            setSelectedIds(new Set());
            setBulkEdit({});
        } catch {
            // Leave selection intact so the user can retry; caller (handleDrawerSave) shows the toast.
        } finally {
            setSaving(false);
        }
    };

    const heading = (
        <span className={styles.DrawerHeadingWrap}>
            <strong className={styles.DrawerHeading}>Transactions</strong>
            <span className={styles.DrawerHeadingName}>{` | ${entityName}`}</span>
        </span>
    );

    const headerActions = !loading && transactions.length > 0 && (
        <div className={styles.SearchBox}>
            <input
                type="text"
                className={styles.SearchInput}
                placeholder="Search Transaction Notes"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                aria-label="Search Transaction Notes"
            />
            {searchText ? (
                <button type="button" className={styles.SearchClear} onClick={() => setSearchText('')} aria-label="Clear search">
                    ×
                </button>
            ) : (
                <span className={styles.SearchMagnifier}>
                    <SearchIcon stroke="currentColor" />
                </span>
            )}
        </div>
    );

    const toolbar = !loading && transactions.length > 0 && (
        <div className={styles.Toolbar}>
            <TimePeriodFilter
                startMonth={dateRange.startMonth}
                endMonth={dateRange.endMonth}
                startPickerMin={dateRange.startPickerMin}
                startPickerMax={dateRange.startPickerMax}
                endPickerMin={dateRange.endPickerMin}
                endPickerMax={dateRange.endPickerMax}
                rangeApplied={dateRange.rangeApplied}
                onSelectStartMonth={filterTracking.selectStartMonth}
                onSelectEndMonth={filterTracking.selectEndMonth}
                onClear={dateRange.clear}
            />
            <TypeSelector
                testId="drill-down-range-toggle"
                options={CFB_QUICK_RANGES}
                selectedOptionId={dateRange.activeQuickRange}
                changeSelectedOption={filterTracking.selectQuickRange}
            />
        </div>
    );

    return (
        <BottomDrawer
            heading={heading}
            headerActions={headerActions}
            showHeading
            allowClose
            onClose={handleClose}
            height="95%"
            style={{ maxHeight: '95%' }}
        >
            <div className={styles.DrawerContent}>
                {loading ? (
                    <LoadingSpinner height="40px" color="black" style={{ marginTop: '2rem' }} />
                ) : transactions.length === 0 ? (
                    <div className={styles.EmptyState}>No transactions found.</div>
                ) : (
                    <>
                        {toolbar}
                        <div className={styles.TableWrap}>
                            <table className={styles.Table}>
                                <thead>
                                    <tr>
                                        {canEditFull &&
                                            (() => {
                                                const allSelected =
                                                    visibleTxnIds.length > 0 && visibleTxnIds.every((id) => selectedIds.has(id));
                                                const someSelected = visibleTxnIds.some((id) => selectedIds.has(id));
                                                return (
                                                    <th className={styles.CheckboxCol}>
                                                        <input
                                                            type="checkbox"
                                                            checked={allSelected}
                                                            ref={(el) => {
                                                                if (el) el.indeterminate = someSelected && !allSelected;
                                                            }}
                                                            onChange={() => toggleIdGroup(visibleTxnIds)}
                                                            aria-label="Select all transactions"
                                                        />
                                                    </th>
                                                );
                                            })()}
                                        <th className={styles.DateCol}>
                                            <span className={styles.SortableHeader} onClick={() => onSort(DRILL_DOWN_SORT_FIELDS.DATE)}>
                                                Txn. Date
                                                <SortIcon
                                                    field={DRILL_DOWN_SORT_FIELDS.DATE}
                                                    sortConfig={sortConfig}
                                                    onSort={onSort}
                                                    alwaysVisible
                                                />
                                            </span>
                                        </th>
                                        <th className={styles.CreditsCol}>
                                            <span className={styles.SortableHeader} onClick={() => onSort(DRILL_DOWN_SORT_FIELDS.CREDITS)}>
                                                Credits (₹)
                                                <SortIcon
                                                    field={DRILL_DOWN_SORT_FIELDS.CREDITS}
                                                    sortConfig={sortConfig}
                                                    onSort={onSort}
                                                    alwaysVisible
                                                />
                                            </span>
                                        </th>
                                        <th className={styles.DebitCol}>
                                            <span className={styles.SortableHeader} onClick={() => onSort(DRILL_DOWN_SORT_FIELDS.DEBITS)}>
                                                Debit (₹)
                                                <SortIcon
                                                    field={DRILL_DOWN_SORT_FIELDS.DEBITS}
                                                    sortConfig={sortConfig}
                                                    onSort={onSort}
                                                    alwaysVisible
                                                />
                                            </span>
                                        </th>
                                        <th>Transactions Notes</th>
                                        <th>Tagged Entity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.map((row) =>
                                        row.kind === 'txn' ? (
                                            <tr key={`txn-${row.txn.id}`}>
                                                {canEditFull && (
                                                    <td className={styles.CheckboxCol}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedIds.has(row.txn.id)}
                                                            onChange={() => toggleTxn(row.txn.id)}
                                                            aria-label={`Select transaction ${row.txn.id}`}
                                                        />
                                                    </td>
                                                )}
                                                <td className={styles.DateCol}>{formatDayShortMonthYear(row.txn.date) ?? row.txn.date}</td>
                                                <td className={styles.CreditsCol}>
                                                    {row.txn.transactionType !== 'debit' ? formatTxnAmount(row.txn.amount) : '-'}
                                                </td>
                                                <td className={styles.DebitCol}>
                                                    {row.txn.transactionType === 'debit' ? formatTxnAmount(row.txn.amount) : '-'}
                                                </td>
                                                <td>{row.txn.transactionNote}</td>
                                                <td>
                                                    {row.txn.taggedEntities?.length > 0
                                                        ? row.txn.taggedEntities.map((name) => (
                                                              <span key={name} className={styles.EntityChip}>
                                                                  {name}
                                                              </span>
                                                          ))
                                                        : null}
                                                </td>
                                            </tr>
                                        ) : showSubtotals ? (
                                            <tr key={`sub-${row.group.monthKey || row.group.monthLabel}`} className={styles.SubtotalRow}>
                                                {canEditFull && (
                                                    <td className={styles.CheckboxCol}>
                                                        {row.group.monthKey &&
                                                            (() => {
                                                                const monthIds = row.group.transactions.map((t) => t.id);
                                                                const allSelected =
                                                                    monthIds.length > 0 && monthIds.every((id) => selectedIds.has(id));
                                                                return (
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={allSelected}
                                                                        onChange={() => toggleMonthGroup(row.group.monthKey)}
                                                                        aria-label={`Select all transactions for ${row.group.monthLabel}`}
                                                                    />
                                                                );
                                                            })()}
                                                    </td>
                                                )}
                                                <td>{`Total ${row.group.monthLabel}`}</td>
                                                <td>{formatAmount(row.group.credits)}</td>
                                                <td>{formatAmount(row.group.debits)}</td>
                                                <td>{`Net Amount: ${formatNetAmount(row.group.credits - row.group.debits)}`}</td>
                                                <td></td>
                                            </tr>
                                        ) : null,
                                    )}
                                    <tr className={clsx(styles.SubtotalRow, styles.GrandTotalRow)}>
                                        {canEditFull && <td className={styles.CheckboxCol} />}
                                        <td>Grand Total</td>
                                        <td>{formatAmount(filteredTotals.credits)}</td>
                                        <td>{formatAmount(filteredTotals.debits)}</td>
                                        <td>{`Net Amount: ${formatNetAmount(filteredTotals.net)}`}</td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        {canEditFull && selectedIds.size > 0 && (
                            <DrawerBulkEditFooter
                                selectedCount={selectedIds.size}
                                overLimit={selectedIds.size > MAX_BULK_SELECTION}
                                edit={bulkEdit}
                                orgEntities={orgEntities}
                                rationaleOptions={rationaleOptions}
                                saving={saving}
                                onEditChange={(patch) => setBulkEdit((prev) => ({ ...prev, ...patch }))}
                                onCancel={handleCancel}
                                onSave={handleSave}
                            />
                        )}
                    </>
                )}
            </div>
        </BottomDrawer>
    );
}

export default EntityDrillDownDrawer;
